import React, { useEffect, useMemo, useState } from "react";
import { Elements, useElements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import styled from "@emotion/styled";
import { QueryResult } from "react-query";
import { boolean, object, string } from "yup";
import {
  BusinessPlanOptions,
  FamilyPlanOptions,
  countryOptions,
  toCurrency,
  projectLocationAddonMapper,
  Location,
  Country,
  ReferralSourceEnum,
  putCommasIntoNumber,
  CampaignSourceEnum,
  CouponCodeResult,
  CAPTCHA_ACTIONS,
} from "@ecologi/shared";
import { Select } from "../Select";
import { Button } from "../Button";
import { Text } from "../Text";
import { TextField } from "../TextField";
import { useRecaptcha } from "../Recaptcha";
import {
  FormLayout,
  FormItem,
  RowItems,
  RowItem,
} from "../FormLayout/FormLayout.styles";
import { Typography } from "../Typography";
import { InlineBanner } from "../InlineBanner";
import { selectPlanData } from "./selectors";
import { TermsAcceptance } from "../TermsAcceptance";
import { useForm } from "../utils/use-form";
import { locationCodeLabelMapper, referralSourceOptions } from "./fields";
import styles from "./styles/CheckoutForm.module.scss";
import {
  StripeCardNumber,
  StripeCardExpiry,
  StripeCardCVC,
} from "../StripeElements";
import { CouponCodeField } from "../CouponCodeField";
import { OnSubmitCheckoutFormProps } from "./utils/on-submit-checkout-form";
import { FormFooter } from "../FormFooter";

const CheckoutFAQ = styled.div`
  margin-top: 30px;
`;

const FormFooterContainer = styled.div`
  margin-top: 3rem;
  margin-bottom: 1.2rem;
`;

export type CheckoutFormBaseProps = {
  numLocalTrees: number;
  selectedPlanAddons?: FamilyPlanOptions | BusinessPlanOptions;
  selectedPlanId: string | null;
};

export type CheckoutFormProps = {
  campaignSource?: CampaignSourceEnum;
  couponCodeResult: QueryResult<CouponCodeResult>;
  errorMessage: string;
  isCouponRedemptionEnabled?: boolean;
  isLoading: boolean;
  isProduction?: boolean;
  location: Location | null;
  onCouponCodeChange: (couponCode?: string) => any;
  onSubmit: (args: OnSubmitCheckoutFormProps) => Promise<any>;
  stripe: Stripe | null;
  subscribeUser: any;
} & CheckoutFormBaseProps;

export type CheckoutFormFields = {
  campaignSource?: string;
  country: Country;
  couponCode: string;
  emailAddress: string;
  firstName: string;
  groupName?: string;
  isMarketingAccepted: boolean;
  isTermsAndConditionsAccepted: boolean;
  lastName: string;
  password: string;
  postCode: string;
  referralSource: string;
  testClockId?: string;
};

const getSchema = (
  hasGroupName: boolean,
  countryDefault: Country,
  campaignSource?: CampaignSourceEnum
) =>
  object().shape({
    emailAddress: string()
      .default("")
      .email("Please use a valid email address")
      .required("Email address is a required field"),
    password: string()
      .default("")
      .min(8, "Please use 8 or more characters")
      .required("Password is a required field"),
    ...(hasGroupName
      ? {
          groupName: string()
            .default("")
            .max(50, "Please use less than 50 characters")
            .required(),
        }
      : {}),
    firstName: string().default("").required(),
    lastName: string().default("").required(),
    postCode: string().default("").required(),
    country: string().default(countryDefault).required(),
    couponCode: string().default("").optional(),
    referralSource: string().default(ReferralSourceEnum.UNKNOWN).optional(),
    campaignSource: string().default(campaignSource).optional(),
    isTermsAndConditionsAccepted: boolean().default(false).isTrue(),
    isMarketingAccepted: boolean().default(false).isTrue(),
    testClockId: string().optional(),
  });

export const DumbCheckoutForm: React.FC<
  CheckoutFormProps & { selectedPlanId: string }
> = ({
  campaignSource,
  couponCodeResult,
  errorMessage,
  isCouponRedemptionEnabled,
  isLoading,
  isProduction,
  location,
  numLocalTrees,
  onCouponCodeChange,
  onSubmit,
  selectedPlanAddons,
  selectedPlanId,
  subscribeUser,
}) => {
  const { selectedPlan, currencySymbol, isFamilyPlan, isBusinessPlan } =
    useMemo(
      () => selectPlanData(location, selectedPlanId, selectedPlanAddons),
      [location, selectedPlanId, selectedPlanAddons]
    );

  const elements = useElements();

  const recaptcha = useRecaptcha();

  const [isCardNumberValid, setCardNumberIsValid] = useState(false);
  const [isCardCVCValid, setCardCVCIsValid] = useState(false);
  const [isCardExpiryValid, setCardExpiryIsValid] = useState(false);

  const [isCouponValid, setIsCouponValid] = useState(false);
  const [isCouponStatusUncertain, setIsCouponStatusUncertain] = useState(true);

  useEffect(() => {
    setIsCouponValid(!!couponCodeResult.data?.valid);
    setIsCouponStatusUncertain(false);
  }, [couponCodeResult.data]);

  const schema = useMemo(
    () =>
      getSchema(
        isFamilyPlan || isBusinessPlan,
        location
          ? location.countryCode !== "EU"
            ? location.countryCode
            : "GB"
          : "US",
        campaignSource
      ),
    [isFamilyPlan, isBusinessPlan, location, campaignSource]
  );

  const form = useForm<CheckoutFormFields>({
    validationSchema: schema,
    initialValues: ((initial) => ({
      ...initial,
      country: initial.country as Country,
    }))(schema.getDefaultFromShape()),
    onSubmit: async (formValues) => {
      const captchaToken = await recaptcha.execute(
        isBusinessPlan
          ? CAPTCHA_ACTIONS.BUSINESS_SUB
          : CAPTCHA_ACTIONS.INDIVIDUAL_SUB
      );
      onSubmit({
        elements,
        formValues,
        isBusinessPlan,
        isCardCVCValid,
        isCardExpiryValid,
        isCardNumberValid,
        isCouponStatusUncertain,
        isCouponValid,
        isFamilyPlan,
        numLocalTrees,
        selectedPlan,
        selectedPlanAddons,
        selectedPlanId,
        subscribeUser,
        captchaToken,
      });
    },
  });

  if (!selectedPlan) {
    return null;
  }

  const localProjectAddon = location
    ? projectLocationAddonMapper[location.countryCode]
    : null;

  const totalCost =
    (localProjectAddon?.price || 0) * numLocalTrees + selectedPlan.price;

  const totalCostString = `${currencySymbol}${toCurrency(totalCost)}`;

  let confirmText = `You'll pay ${putCommasIntoNumber(
    totalCostString
  )} per month for your `;
  if (isBusinessPlan) {
    confirmText = `${confirmText} custom business plan.`;
  } else if (isFamilyPlan) {
    confirmText = `${confirmText} custom family plan.`;
  } else {
    confirmText = `${confirmText} ${selectedPlan.title.toLowerCase()} plan.`;
  }

  const locationCodeLabel =
    location?.countryCode == "US"
      ? locationCodeLabelMapper.US
      : locationCodeLabelMapper.default;

  return (
    <>
      <Text
        isCenter
        isHeading
        style={{ marginBottom: "32px" }}
        text="Set up your account"
      />
      <form
        id="checkout-form"
        className={isLoading ? styles.formDisabled : ""}
        onSubmit={form.handleSubmit}
      >
        <FormLayout>
          <RowItems>
            <RowItem>
              <TextField
                {...form.getFieldProps("emailAddress", "Email address")}
                type="email"
                errorText={form.errors.emailAddress?.toString()}
              />
            </RowItem>
            <RowItem>
              <TextField
                {...form.getFieldProps("password", "Password")}
                type="password"
                errorText={form.errors.password?.toString()}
              />
            </RowItem>
          </RowItems>
          <Text
            isCenter
            isSubheading
            style={{
              borderTop: "2px solid #f2f2f2",
              fontWeight: "bold",
              marginBottom: "20px",
              marginTop: "50px",
              paddingTop: "32px",
            }}
            text="Next, we need a few details"
          />
          {(isFamilyPlan || isBusinessPlan) && (
            <RowItems>
              <FormItem>
                <TextField
                  {...form.getFieldProps(
                    "groupName",
                    isFamilyPlan ? "Family name" : "Business name"
                  )}
                  maxLength={50}
                  showCharacterCount
                  placeholder={
                    isFamilyPlan
                      ? "Example 'The Jacks'"
                      : "Example 'Lucy's Shoes'"
                  }
                  helpText="This will be displayed on your profile"
                />
              </FormItem>
            </RowItems>
          )}
          <RowItems>
            <RowItem>
              <TextField {...form.getFieldProps("firstName", "First name")} />
            </RowItem>
            <RowItem>
              <TextField {...form.getFieldProps("lastName", "Last name")} />
            </RowItem>
          </RowItems>
          <RowItems>
            <RowItem>
              <TextField
                {...form.getFieldProps("postCode", locationCodeLabel)}
              />
            </RowItem>
            <RowItem>
              <Select
                {...form.getFieldProps("country", "Country")}
                items={countryOptions}
              />
            </RowItem>
          </RowItems>
          <RowItem>
            <StripeCardNumber
              onChange={(e) => setCardNumberIsValid(e.complete)}
              isDisabled={isLoading}
              label="Enter your card details"
            />
          </RowItem>
          <RowItems>
            <RowItem>
              <StripeCardExpiry
                onChange={(e) => setCardExpiryIsValid(e.complete)}
                isDisabled={isLoading}
                label="Expiry date"
              />
            </RowItem>
            <RowItem>
              <StripeCardCVC
                onChange={(e) => setCardCVCIsValid(e.complete)}
                isDisabled={isLoading}
                label="CVC"
              />
            </RowItem>
          </RowItems>
        </FormLayout>
        {isCouponRedemptionEnabled && (
          <CouponCodeField
            couponCodeResult={couponCodeResult}
            currencySymbol={currencySymbol}
            onBlur={form.handleBlur}
            onChange={(e) => {
              setIsCouponStatusUncertain(true);
              onCouponCodeChange(e.target.value);
              form.handleChange(e);
            }}
            value={form.values["couponCode"]}
            isCouponValid={isCouponValid}
            isCouponStatusUncertain={isCouponStatusUncertain}
          />
        )}
        {errorMessage && (
          <div
            style={{ position: "relative", top: isBusinessPlan ? "15px" : "0" }}
          >
            <InlineBanner title="There was an error!" status="critical">
              <p>{errorMessage}</p>
            </InlineBanner>
            <br />
          </div>
        )}
        <p className={styles.checkoutConfirmText}>{confirmText}</p>
        <div className={styles.referralSourceGroup}>
          <Select
            {...form.getFieldProps(
              "referralSource",
              "Where did you hear about Ecologi?"
            )}
            data-testid="referral-source-select"
            items={referralSourceOptions}
            id="referralSource"
          />
        </div>
        <TermsAcceptance
          isLoading={isLoading}
          isTermsChecked={form.values["isTermsAndConditionsAccepted"]}
          isEmailTermsChecked={form.values["isMarketingAccepted"]}
          disableEmailCheckbox
          setIsTermsChecked={(isChecked) => {
            form.handleChange({
              target: {
                name: "isTermsAndConditionsAccepted",
                value: isChecked,
              },
            });
          }}
          setIsEmailTermsChecked={(isChecked) =>
            form.handleChange({
              target: {
                name: "isMarketingAccepted",
                value: isChecked,
              },
            })
          }
        />

        {!isProduction && (
          <div style={{ marginBottom: "20px" }}>
            <TextField
              {...form.getFieldProps("testClockId", "Test Clock ID")}
              helpText="Enter a Stripe test clock ID to attach this user to a test clock"
            />
          </div>
        )}

        <Button
          disabled={!form.isValid || form.isSubmitting}
          extra={{ style: { margin: "0 auto" } }}
          loading={isLoading || form.isSubmitting}
          primary
          submit
          text="Subscribe and pay"
        />
        <FormFooterContainer>
          <FormFooter showRecaptchaPolicy />
        </FormFooterContainer>
        <CheckoutFAQ>
          <Typography as="h3" use="body2">
            Can I change or cancel my plan?
          </Typography>
          <Typography as="p" use="body1">
            Yes, you can change your plan at any time. If you'd like to cancel
            your subscription or switch to a new plan, you can do easily when
            editing your profile or messaging us directly.
          </Typography>
        </CheckoutFAQ>
        <CheckoutFAQ>
          <Typography as="h3" use="body2">
            Powered by Stripe
          </Typography>
          <Typography as="p" use="body1">
            This page uses Stripe's secure checkout. By using it you're
            accepting their{" "}
            <Typography
              as="a"
              href="https://stripe.com/checkout/terms"
              rel="noopener noreferrer"
              target="_blank"
              title="Review Stripe's Ts&amp;Cs"
              use="inlineLink"
            >
              Terms &amp; Conditions
            </Typography>
            {" and "}
            <Typography
              as="a"
              href="https://stripe.com/privacy"
              rel="noopener noreferrer"
              target="_blank"
              title="Review Stripe's Privacy Policy"
              use="inlineLink"
            >
              Privacy Policy
            </Typography>
            .
          </Typography>
        </CheckoutFAQ>
      </form>
    </>
  );
};

const withEmptyPlanIdWrapper =
  (Component: React.ComponentType<CheckoutFormProps>) =>
  (props: CheckoutFormProps) => {
    const { stripe } = props;
    if (!props.selectedPlanId) {
      return (
        <div>
          An error has occurred. Please refresh the page and try again
          <br />
          <br />
          <Button
            text="Refresh"
            onClick={(e) => {
              e.preventDefault();
              window.location.reload();
            }}
          />
        </div>
      );
    }

    return (
      <Elements stripe={stripe}>
        <Component {...props} />
      </Elements>
    );
  };

export const CheckoutForm = withEmptyPlanIdWrapper(DumbCheckoutForm);
